import { z } from 'zod';
import { baseMessageSchema } from './_base';
import { clientTypeSchema } from './shared';

export enum PresenceMessageType {
  PRESENCE_UPDATE = 'PRESENCE_UPDATE',
  PRESENCE_UPDATE_SUCCESS = 'PRESENCE_UPDATE_SUCCESS',
  PRESENCE_UPDATE_ERROR = 'PRESENCE_UPDATE_ERROR',
  PRESENCE_CHANGED = 'PRESENCE_CHANGED',
}

export const hostStatusSchema = z.enum(['offline', 'idle', 'hosting', 'busy']);
export const playerStatusSchema = z.enum(['idle', 'waiting', 'joined']);

export const hostGameSchema = z.object({
  type: z.enum(['steam', 'epic', 'microsoft', 'exe']),
  id: z.string(),
});

export type HostStatus = z.infer<typeof hostStatusSchema>;
export type PlayerStatus = z.infer<typeof playerStatusSchema>;
export type Game = z.infer<typeof hostGameSchema>;
export type HostPresence = z.infer<typeof hostPresenceSchema>;

// Common schemas for presence data
const playerPresenceSchema = z.object({
  status: playerStatusSchema,
  currentLobby: z.string().optional(),
  lobbyPosition: z.number().optional(),
});

const hostPresenceSchema = z.object({
  status: hostStatusSchema,
  playerCount: z.number(),
  runningGame: hostGameSchema.optional(),
  hostPeerId: z.string().optional(),
  remoteHostPeerId: z.string().optional(),
  lastUpdated: z.number(),
});

// Payload schemas
const presenceUpdatePayloadSchema = z.object({
  clientType: clientTypeSchema,
  presence: z.union([hostPresenceSchema, playerPresenceSchema]),
});

const presenceUpdateSuccessPayloadSchema = z.object({
  acknowledged: z.boolean(),
});

const presenceUpdateErrorPayloadSchema = z.object({
  error: z.object({
    code: z.string(),
    message: z.string(),
  }),
});

const presenceChangedPayloadSchema = z.object({
  clientId: z.string(), // MachineId or AuthId / PersistentId
  clientType: clientTypeSchema,
  presence: z.union([hostPresenceSchema, playerPresenceSchema]),
});

// Full message schemas
const presenceUpdateSchema = baseMessageSchema
  .extend({
    type: z.literal(PresenceMessageType.PRESENCE_UPDATE),
  })
  .merge(presenceUpdatePayloadSchema);

const presenceUpdateSuccessSchema = baseMessageSchema
  .extend({
    type: z.literal(PresenceMessageType.PRESENCE_UPDATE_SUCCESS),
  })
  .merge(presenceUpdateSuccessPayloadSchema);

const presenceUpdateErrorSchema = baseMessageSchema
  .extend({
    type: z.literal(PresenceMessageType.PRESENCE_UPDATE_ERROR),
  })
  .merge(presenceUpdateErrorPayloadSchema);

const presenceChangedSchema = baseMessageSchema
  .extend({
    type: z.literal(PresenceMessageType.PRESENCE_CHANGED),
  })
  .merge(presenceChangedPayloadSchema);

export const presenceMessageSchema = z.discriminatedUnion('type', [
  presenceUpdateSchema,
  presenceUpdateSuccessSchema,
  presenceUpdateErrorSchema,
  presenceChangedSchema,
]);

// Export payload types
export type PresenceUpdatePayload = z.infer<typeof presenceUpdatePayloadSchema>;
export type PresenceUpdateSuccessPayload = z.infer<typeof presenceUpdateSuccessPayloadSchema>;
export type PresenceUpdateErrorPayload = z.infer<typeof presenceUpdateErrorPayloadSchema>;
export type PresenceChangedPayload = z.infer<typeof presenceChangedPayloadSchema>;

// Full message type
export type PresenceMessage = z.infer<typeof presenceMessageSchema>;

type PayloadType<T extends PresenceMessageType> = T extends PresenceMessageType.PRESENCE_UPDATE
  ? PresenceUpdatePayload
  : T extends PresenceMessageType.PRESENCE_UPDATE_SUCCESS
    ? PresenceUpdateSuccessPayload
    : T extends PresenceMessageType.PRESENCE_UPDATE_ERROR
      ? PresenceUpdateErrorPayload
      : T extends PresenceMessageType.PRESENCE_CHANGED
        ? PresenceChangedPayload
        : never;

export function generatePresenceMessage<T extends PresenceMessageType>(type: T, payload: PayloadType<T>): PresenceMessage {
  const base = {
    id: crypto.randomUUID(),
    timestamp: Date.now(),
    type,
  };

  switch (type) {
    case PresenceMessageType.PRESENCE_UPDATE:
      return presenceUpdateSchema.parse({
        ...base,
        ...payload,
      });
    case PresenceMessageType.PRESENCE_UPDATE_SUCCESS:
      return presenceUpdateSuccessSchema.parse({
        ...base,
        ...payload,
      });
    case PresenceMessageType.PRESENCE_UPDATE_ERROR:
      return presenceUpdateErrorSchema.parse({
        ...base,
        ...payload,
      });
    case PresenceMessageType.PRESENCE_CHANGED:
      return presenceChangedSchema.parse({
        ...base,
        ...payload,
      });
    default: {
      const exhaustiveCheck: never = type;
      throw new Error(`Unhandled message type: ${exhaustiveCheck}`);
    }
  }
}
